import { createAction, handleActions } from 'redux-actions';

import { Account, Timezone } from '@adjust/dashboard-core/api/types';
import { Trace, TraceStatus } from '@redux/types';

interface AccountDetails {
  isLoading: boolean;
  isSubmitting: boolean;
  isAlertOpen: boolean;
  trace: Trace;
}

export const initialState: AccountDetails = {
  isLoading: false,
  isSubmitting: false,
  isAlertOpen: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
};

// Actions

export const DATA_FETCH_STARTED = 'accountDetails/DATA_FETCH_STARTED';
export const DATA_FETCH_SUCCEEDED = 'accountDetails/DATA_FETCH_SUCCEEDED';
export const DATA_UPDATE_STARTED = 'accountDetails/DATA_UPDATE_STARTED';
export const DATA_UPDATE_FAILED = 'accountDetails/DATA_UPDATE_FAILED';
export const DATA_UPDATE_SUCCEEDED = 'accountDetails/DATA_UPDATE_SUCCEEDED';
export const ALERT_CLOSE = 'accountDetails/ALERT_CLOSE';

// Action creators

export const fetchData = createAction(DATA_FETCH_STARTED);
export const fetchDataOk = createAction(DATA_FETCH_SUCCEEDED);
export const updateData = createAction(DATA_UPDATE_STARTED);
export const updateDataError = createAction(DATA_UPDATE_FAILED);
export const updateDataOk = createAction(DATA_UPDATE_SUCCEEDED);
export const closeAlert = createAction(ALERT_CLOSE);

// Reducer

const handleFetchStarted = (state: AccountDetails): AccountDetails => ({
  ...state,
  isLoading: true,
});

const handleFetchSucceeded = (state: AccountDetails): AccountDetails => ({
  ...state,
  isLoading: false,
});

const handleUpdateStarted = (state: AccountDetails): AccountDetails => ({
  ...state,
  isSubmitting: true,
});

const handleUpdateFailed = (state: AccountDetails): AccountDetails => ({
  ...state,
  isSubmitting: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.try-again-later'),
  },
});

const handleUpdateSucceeded = (state: AccountDetails): AccountDetails => ({
  ...state,
  isSubmitting: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: t('message.changes-saved-successfully'),
  },
});

const handleAlertClose = (state: AccountDetails): AccountDetails => ({
  ...state,
  isAlertOpen: false,
});

export default handleActions(
  {
    [DATA_FETCH_STARTED]: handleFetchStarted,
    [DATA_FETCH_SUCCEEDED]: handleFetchSucceeded,
    [DATA_UPDATE_STARTED]: handleUpdateStarted,
    [DATA_UPDATE_FAILED]: handleUpdateFailed,
    [DATA_UPDATE_SUCCEEDED]: handleUpdateSucceeded,
    [ALERT_CLOSE]: handleAlertClose,
  },
  initialState,
);

// Selectors

export type TimezoneOption = Timezone & { value: number };

export const getTimezonesOptions = (state): TimezoneOption[] => {
  const { timezones } = state.global;

  return (
    timezones &&
    timezones.map(timezone => ({
      value: timezone.id,
      ...timezone,
    }))
  );
};

export const getAccount = (state): Account => {
  const { account } = state.global;

  return (
    account && {
      ...account,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      address_attributes: {
        ...account.address,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        country_code: account.address.country_code.toLowerCase(),
      },
    }
  );
};

export const getAccountDetailsState = (state): AccountDetails => state.accountDetails;
